import { WEBP, NON_CONVERTIBLE_EXTENSIONS } from '../../../constants';

const PATH_DELIMITER = '/';
const EXTENSION_DELIMITER = '.';

function getExtension(pathname) {
  return pathname.split(EXTENSION_DELIMITER).pop().toLowerCase();
}

/**
 * Rewrites a CDN converted image path to its webp equivalent.
 *
 * `useSingleImageUrl` never asks the CDN for a format, so it can only return the source
 * format. This mirrors the `format` branch of `getConvertedImagePath` in
 * `@blaze-cms/image-cdn-react` (filename up to the first dot, plus `.webp`) so the path
 * matches the one the CDN already serves for `ResponsiveImage`.
 *
 * @param {string} src original image url
 * @param {string} convertedUrl url returned by `useSingleImageUrl`
 * @returns {string} the webp path, or an unconverted url when it cannot be derived safely
 */
const getWebpPath = (src, convertedUrl) => {
  // `getSingleConvertedImagePath` returns the source untouched when there is no matching
  // size config. Swapping the extension there would point at a file that does not exist,
  // and a css background has no `<picture>` fallback to recover with
  if (!src || !convertedUrl || convertedUrl === src) return convertedUrl;

  try {
    const { pathname: sourcePathname } = new URL(src);
    if (NON_CONVERTIBLE_EXTENSIONS.includes(getExtension(sourcePathname))) return src;

    const parsedUrl = new URL(convertedUrl);
    const segments = parsedUrl.pathname.split(PATH_DELIMITER);
    const [imageName] = segments[segments.length - 1].split(EXTENSION_DELIMITER);
    if (!imageName) return convertedUrl;

    segments[segments.length - 1] = `${imageName}${EXTENSION_DELIMITER}${WEBP}`;
    parsedUrl.pathname = segments.join(PATH_DELIMITER);
    return parsedUrl.toString();
  } catch (error) {
    return convertedUrl;
  }
};

export default getWebpPath;
